import { Circle } from "@mui/icons-material";
import { Box, Divider, Stack, SxProps, Theme, Typography } from "@mui/material";
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts";
import theme from "../../App/Styles/theme";
import { DiagonalXAxisTick } from "../Components/DiagonalXAxisTick";
import ReportContainer from "../Components/ReportContainer";
import { getThemedColorCode } from "../Helpers/ThemedColorCodes";
import { CandidatesAddedByCampaignReportState } from "./State/CandidatesAddedByCampaignReportState";

export const middleLineColor = "#556EC5";

const CustomToolTip = (props: any) => {
  const { active, payload } = props;

  if (active && payload?.length) {
    let dayTotal = 0;

    return (
      <Box
        sx={{
          width: 200,
          margin: 0,
          lineHeight: "24px",
          backgroundColor: "#fff",
          border: "1px solid #000",
          padding: "10px",
        }}
      >
        <Box sx={{ pb: 2 }}>
          <Typography fontWeight={700}>{payload[0].payload.dateAdded}</Typography>
        </Box>
        <Box sx={{ pb: 2 }}>
          {payload.map((barSection: { fill: any; dataKey: any; value: number }, index: number) => {
            dayTotal += Number(barSection.value) ?? 0;
            return (
              <Typography
                sx={{ color: barSection.fill }}
                fontWeight={500}
                id={`candidatesbycampaign-tooltipitem-${index}`}
                key={`candidatesbycampaign-tooltipitem-${index}`}
                data-test={`tooltip-section-${barSection.dataKey}`}
              >
                {barSection.dataKey}: {barSection.value}
              </Typography>
            );
          })}
        </Box>
        <Divider />
        <Typography sx={{ pt: 2 }} data-test={`daily-total`}>
          Daily Total: {dayTotal}
        </Typography>
      </Box>
    );
  }

  return null;
};

const CustomLegend = (props: any) => {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      columnGap={2}
      rowGap={1}
      flexWrap="wrap"
    >
      {props.payload.map((entry: any, index: number) => {
        return (
          <Stack
            id={`candidatesbycampaign-legenditem-${index}`}
            key={`candidatesbycampaign-legenditem-${index}`}
            title={entry.value}
            direction="row"
            alignItems="center"
            gap={0.5}
          >
            <Circle sx={{ color: entry.color }} fontSize="small" />
            <Typography color={entry.color} key={`item-${index}`} fontWeight="bold" sx={customLegendStyles}>
              {entry.value}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export const customLegendStyles = {
  width: "100%",
  maxWidth: { xs: "calc(100vw - 8rem)", md: "calc(50vw - 6rem)", lg: "calc(25vw - 6rem)" },
  overflow: "hidden",
  textWrap: "nowrap",
  textOverflow: "ellipsis",
} as SxProps<Theme>;

export const CandidatesAddedByCampaignReport = ({
  state,
}: {
  state: CandidatesAddedByCampaignReportState;
}) => {
  const {
    getters: { reportData, isLoading, isReportEnabled },
  } = state;
  if (!isReportEnabled) {
    return null;
  }

  return (
    <ReportContainer
      id="candidates-by-campaigns"
      title="Top 5 Forms by Candidates Added"
      isLoading={isLoading}
      hasLegend={true}
    >
      <AreaChart
        data={reportData?.tableData}
        style={{
          position: "relative",
        }}
        margin={{
          top: 0,
          right: 30,
          left: 20,
          bottom: 50,
        }}
      >
        <defs>
          {reportData?.campaignsInReport.map((c, idx) => (
            <linearGradient id={`area-gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={getThemedColorCode(idx)} stopOpacity={0.3} />
              <stop offset="95%" stopColor={getThemedColorCode(idx)} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke="#eeeded" vertical={false} />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey="dateAdded"
          tick={<DiagonalXAxisTick />}
          tickFormatter={(value: any) => new Date(value).toLocaleDateString()}
        />
        <YAxis axisLine={false} tickLine={false} type="number" domain={["auto", "dataMax"]} />
        <Tooltip
          content={<CustomToolTip />}
          cursor={{ stroke: theme.palette.secondary.main, strokeDasharray: 5 }}
        />
        <Legend
          layout="horizontal"
          verticalAlign="top"
          align="center"
          content={<CustomLegend />}
          wrapperStyle={{ paddingBottom: 30 }}
          payload={reportData?.campaignsInReport.map((campaign, index) => ({
            id: campaign.name,
            type: "square",
            value: campaign.name,
            candidateCount: campaign.countInReportPeriod,
            color: getThemedColorCode(index),
          }))}
        />
        {reportData?.campaignsInReport.map((c, idx) => (
          <Area
            type="monotone"
            dataKey={c.name}
            stroke={getThemedColorCode(idx)}
            strokeWidth={3}
            fillOpacity={1}
            fill={`url(#area-gradient-${idx})`}
            activeDot={{ r: 6 }}
          />
        ))}
      </AreaChart>
    </ReportContainer>
  );
};
